import { useState, useEffect, useCallback } from 'react'
import { supabase } from '../lib/supabase'
import { useTranslation } from '../locales/useTranslation'
import BookingCard from '../components/bookings/BookingCard'
import RequestCard from '../components/bookings/RequestCard'
import {
  sendBookingAcceptedEmail,
  sendBookingRejectedEmail,
  sendBookingCancelledEmail
} from '../lib/emailService'

const BookingsPage = ({ session, showToast }) => {
  const { t } = useTranslation()
  const [myBookings, setMyBookings] = useState([])
  const [incomingRequests, setIncomingRequests] = useState([])
  const [initialLoaded, setInitialLoaded] = useState(false)
  const [activeSection, setActiveSection] = useState('boekingen')

  const fetchAll = useCallback(async () => {
    try {
      const [{ data: driverBookings, error: e1 }, { data: ownerRequests, error: e2 }] =
        await Promise.all([
          supabase
            .from('bookings')
            .select(`*, chargers (name, address, city, connector_type, speed_kw, price_per_kwh)`)
            .eq('driver_id', session.user.id)
            .order('created_at', { ascending: false }),
          supabase
            .from('bookings')
            .select(`*, chargers (name, address, city)`)
            .eq('owner_id', session.user.id)
            .order('created_at', { ascending: false })
        ])

      if (e1) throw e1
      if (e2) throw e2

      // Update data silently — no loading state toggle
      setMyBookings(driverBookings || [])
      setIncomingRequests(ownerRequests || [])
    } catch (error) {
      showToast(t('fout'), true)
    } finally {
      setInitialLoaded(true)
    }
  }, [session.user.id, showToast, t])

  useEffect(() => { fetchAll() }, [fetchAll])

  const handleRespond = async (bookingId, status, response) => {
    try {
      const { error } = await supabase
        .from('bookings')
        .update({ status, owner_response: response || null })
        .eq('id', bookingId)

      if (error) throw error

      await supabase.from('events').insert({
        user_id: session.user.id,
        event_type: status === 'geaccepteerd' ? 'booking_accepted' : 'booking_rejected',
        entity_type: 'booking',
        entity_id: bookingId,
        metadata: { status }
      })

      const booking = incomingRequests.find(r => r.id === bookingId)
      if (booking) {
        const { data: driverProfile } = await supabase
          .from('users')
          .select('full_name')
          .eq('id', booking.driver_id)
          .single()

        if (status === 'geaccepteerd') {
          await sendBookingAcceptedEmail({
            driverEmail: session.user.email,
            driverName: driverProfile?.full_name,
            chargerName: booking.chargers?.name,
            chargerAddress: booking.chargers?.address,
            chargerCity: booking.chargers?.city,
            date: booking.date,
            startTime: booking.start_time?.slice(0, 5),
            endTime: booking.end_time?.slice(0, 5),
            totalPriceEst: booking.total_price_est,
            ownerResponse: response
          })
        } else {
          await sendBookingRejectedEmail({
            driverEmail: session.user.email,
            driverName: driverProfile?.full_name,
            chargerName: booking.chargers?.name,
            date: booking.date,
            startTime: booking.start_time?.slice(0, 5),
            endTime: booking.end_time?.slice(0, 5),
            ownerResponse: response
          })
        }
      }

      showToast(status === 'geaccepteerd' ? t('boeking_geaccepteerd') : t('boeking_geweigerd'))

      // Update locally first for instant feedback, then sync with server
      setIncomingRequests(prev =>
        prev.map(r => r.id === bookingId
          ? { ...r, status, owner_response: response || null }
          : r
        )
      )
      fetchAll()

    } catch (error) {
      showToast(t('fout'), true)
    }
  }

  const handleCancel = async (bookingId) => {
    try {
      const { error } = await supabase
        .from('bookings')
        .update({
          status: 'geannuleerd',
          cancelled_by: session.user.id,
          cancelled_at: new Date().toISOString()
        })
        .eq('id', bookingId)

      if (error) throw error

      await supabase.from('events').insert({
        user_id: session.user.id,
        event_type: 'booking_cancelled',
        entity_type: 'booking',
        entity_id: bookingId,
        metadata: { cancelled_by: 'driver' }
      })

      const booking = myBookings.find(b => b.id === bookingId)
      if (booking) {
        const { data: ownerProfile } = await supabase
          .from('users')
          .select('full_name')
          .eq('id', booking.owner_id)
          .single()

        await sendBookingCancelledEmail({
          ownerEmail: session.user.email,
          ownerName: ownerProfile?.full_name,
          chargerName: booking.chargers?.name,
          date: booking.date,
          startTime: booking.start_time?.slice(0, 5),
          endTime: booking.end_time?.slice(0, 5)
        })
      }

      showToast(t('boeking_geannuleerd'))

      // Update locally first for instant feedback
      setMyBookings(prev =>
        prev.map(b => b.id === bookingId
          ? { ...b, status: 'geannuleerd', cancelled_by: session.user.id }
          : b
        )
      )
      fetchAll()

    } catch (error) {
      showToast(t('fout'), true)
    }
  }

  const pendingCount = incomingRequests.filter(r => r.status === 'in_behandeling').length

  return (
    <div style={{ height: '100%', overflowY: 'auto', padding: '52px 18px 20px' }}>

      <div style={{
        fontFamily: 'Fraunces, serif', fontSize: 24,
        fontWeight: 900, color: 'var(--ink)', marginBottom: 18
      }}>
        {t('boekingen')}
      </div>

      {/* Section toggle */}
      <div style={{
        display: 'flex', background: 'var(--cream-dark)',
        borderRadius: 12, padding: 4, marginBottom: 20, gap: 4
      }}>
        {[
          { id: 'boekingen', label: t('mijn_boekingen') },
          { id: 'verzoeken', label: t('verzoeken'), badge: pendingCount > 0 ? pendingCount : null }
        ].map(section => (
          <button
            key={section.id}
            onClick={() => setActiveSection(section.id)}
            style={{
              flex: 1, padding: '9px 12px', borderRadius: 10, border: 'none',
              background: activeSection === section.id ? 'white' : 'transparent',
              color: activeSection === section.id ? 'var(--ink)' : 'var(--ink-muted)',
              fontWeight: activeSection === section.id ? 700 : 500,
              cursor: 'pointer', fontSize: 13, fontFamily: 'DM Sans, sans-serif',
              boxShadow: activeSection === section.id ? '0 1px 4px rgba(0,0,0,0.1)' : 'none',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              gap: 6, transition: 'all 0.2s'
            }}>
            {section.label}
            {section.badge && (
              <span style={{
                background: 'var(--green)', color: 'white',
                borderRadius: 20, padding: '1px 7px', fontSize: 11, fontWeight: 800
              }}>
                {section.badge}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Show spinner only on first load, not on refresh */}
      {!initialLoaded ? (
        <div style={{
          textAlign: 'center', padding: '50px 0',
          color: 'var(--green)', fontWeight: 600
        }}>
          {t('laden')}
        </div>
      ) : activeSection === 'boekingen' ? (
        myBookings.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '60px 0' }}>
            <div style={{ fontSize: 48, marginBottom: 12 }}>📅</div>
            <div style={{
              fontFamily: 'Fraunces, serif', fontSize: 18,
              fontWeight: 900, color: 'var(--ink)', marginBottom: 6
            }}>
              {t('geen_boekingen')}
            </div>
            <div style={{ color: 'var(--ink-muted)', fontSize: 14 }}>
              {t('geen_boekingen_sub')}
            </div>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {myBookings.map(booking => (
              <BookingCard key={booking.id} booking={booking} onCancel={handleCancel} />
            ))}
          </div>
        )
      ) : (
        incomingRequests.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '60px 0' }}>
            <div style={{ fontSize: 48, marginBottom: 12 }}>📬</div>
            <div style={{
              fontFamily: 'Fraunces, serif', fontSize: 18,
              fontWeight: 900, color: 'var(--ink)', marginBottom: 6
            }}>
              {t('geen_verzoeken')}
            </div>
            <div style={{ color: 'var(--ink-muted)', fontSize: 14 }}>
              Boekingsverzoeken verschijnen hier
            </div>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {incomingRequests.map(request => (
              <RequestCard key={request.id} request={request} onRespond={handleRespond} />
            ))}
          </div>
        )
      )}
    </div>
  )
}

export default BookingsPage
